<template>
  <div
    class="tier-account-selector"
    :class="{ 'tier-account-selector--small': small }"
  >
    <div ref="box" class="tier-account-selector-box">
      <!-- The arrival of a context switch, drawn as a spark running once around this control --
           the thing on screen, besides the tab, that the switch has just changed. Inside the box
           rather than over it, so that an inset places it with nothing measured and there is no
           second control it could be traced around by mistake. -->
      <svg
        v-if="shimmer"
        class="tier-account-selector-shimmer"
        :width="shimmer.width"
        :height="shimmer.height"
        aria-hidden="true"
        @animationend="onShimmerEnd"
      >
        <!-- The drop shadow the design puts on the spark, as its export writes it: the stroke's
             alpha saturated to opaque, blurred, tinted to #3D8A93 at 60% and laid beneath the
             stroke. The saturation is what makes it visible at all.
             The filter region is the one thing not copied across, the design stating it in
             absolute units for a fixed canvas and this box being measured. -->
        <defs>
          <filter
            id="tierAccountSelectorSparkGlow"
            x="-70%"
            y="-70%"
            width="240%"
            height="240%"
            color-interpolation-filters="sRGB"
          >
            <feFlood flood-opacity="0" result="BackgroundImageFix"></feFlood>
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            ></feColorMatrix>
            <feOffset></feOffset>
            <feGaussianBlur stdDeviation="3"></feGaussianBlur>
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 0.239216 0 0 0 0 0.541176 0 0 0 0 0.576471 0 0 0 0.6 0"
            ></feColorMatrix>
            <feBlend
              mode="normal"
              in2="BackgroundImageFix"
              result="effect1_dropShadow"
            ></feBlend>
            <feBlend
              mode="normal"
              in="SourceGraphic"
              in2="effect1_dropShadow"
              result="shape"
            ></feBlend>
          </filter>
        </defs>
        <path
          :d="shimmer.outline"
          pathLength="1"
          filter="url(#tierAccountSelectorSparkGlow)"
        ></path>
      </svg>
      <span v-if="small" class="tier-account-selector-avatar">{{
        initials
      }}</span>

      <!-- Filtered against the list the store already holds rather than searched against the API:
           that list IS everything reachable at this tier, and it is loaded anyway to know whether
           there is a choice worth offering. -->
      <v-autocomplete
        v-else
        ref="field"
        class="tier-account-selector-field"
        :items="accounts"
        item-text="displayName"
        item-value="id"
        :key="fieldKey"
        :value="value"
        :menu-props="{ offsetY: true, nudgeBottom: 9 }"
        hide-details
        hide-no-data
        dense
        solo
        flat
        @change="enter"
        @blur="fieldKey += 1"
        data-id="c108ad2c18c9406fae940a5500aa02eb"
      >
        <template v-slot:prepend-inner>
          <span class="tier-account-selector-avatar">{{ initials }}</span>
        </template>

        <template v-slot:item="{ item }">
          <span class="tier-account-selector-option">{{
            item.displayName
          }}</span>
        </template>
      </v-autocomplete>
    </div>
  </div>
</template>

<script>
// How far the spark's canvas reaches past the box on every side. The halo is wider than the
// stroke and an svg clips to its own bounds, so the room for it has to be in the canvas: the
// design draws a 214x40 box on a 228x54 one, which is this.
const SPARK_BLEED = 7;

// The account the spark was last drawn for. At module scope rather than on the instance because
// the instance is not what survives a navigation: the drawer is rebuilt whenever the route's
// record changes, so a control asking whether it has already drawn where it is has to ask
// something older than itself.
let lastAccount = null;

// The outline of a rounded box of the given size, as a path, so the dash that travels it can be
// stated as a fraction of the whole and the box can be any size. Left open a hair short of where
// it began rather than closed, as the design draws it: a trim needs a start and an end, and a
// closed path has neither.
function roundedBoxPath(width, height, radius, inset) {
  const right = width - inset;
  const bottom = height - inset;
  const r = Math.max(0, radius);
  const arc = " A " + r + " " + r + " 0 0 1 ";

  return (
    "M " +
    (inset + r) +
    " " +
    inset +
    " H " +
    (right - r) +
    arc +
    right +
    " " +
    (inset + r) +
    " V " +
    (bottom - r) +
    arc +
    (right - r) +
    " " +
    bottom +
    " H " +
    (inset + r) +
    arc +
    inset +
    " " +
    (bottom - r) +
    " V " +
    (inset + r) +
    arc +
    (inset + r - 0.01) +
    " " +
    (inset + 0.01)
  );
}

export default {
  data: () => ({
    // Bumped on blur to rebuild the field, which restores the account in context after anything
    // that led nowhere: text matching nothing, or the box emptied. Cheaper than undoing each case
    // by hand, since the options are already in memory.
    fieldKey: 0,
    // How big to trace the spark, taken from this control's own box and kept in step with it
    // while it plays. Absent the rest of the time, so nothing is drawn.
    shimmer: null,
    shimmerFrame: null,
  }),

  props: {
    // The drawer's collapsed state: the name goes, the avatar stays.
    small: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    accounts() {
      return this.$store.getters.currentTierAccounts;
    },
    account() {
      return this.$store.state.masterAccount;
    },
    value() {
      return this.account ? this.account.id : null;
    },
    activeDisplayName() {
      return this.account ? this.account.displayName : "";
    },
    // Two letters: "Advisory firm 1" reads as AF.
    initials() {
      return this.activeDisplayName
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((word) => word.charAt(0).toUpperCase())
        .join("");
    },
  },

  watch: {
    // Compared by identity rather than by id: the action that moves context answers a request for
    // the account already in context without loading anything, so a new instance is precisely
    // "the context moved". An ordinary navigation hands back the same object, and a return to an
    // account just left hands back a different one under the same id.
    //
    // Immediate because this control does not survive a navigation, so an arrival reaches it
    // either as a change under an instance that lived through it or as the first value an
    // instance is born holding.
    account: {
      immediate: true,
      handler: function (account) {
        if (!account || account === lastAccount) return;

        this.scheduleShimmer();
      },
    },
  },

  beforeDestroy: function () {
    this.stopFollowingBox();
  },

  methods: {
    // Two frames rather than a tick: the box has to be laid out before it can be measured, and
    // a tick lands before the browser has done that.
    scheduleShimmer: function () {
      requestAnimationFrame(() => requestAnimationFrame(this.playShimmer));
    },

    playShimmer: function () {
      const traced = this.traceBox();

      // Nothing to draw around. Either this control has not laid out yet, or -- the case that
      // matters -- it is the one the navigation is leaving.
      //
      // The account in context is committed while the page being left is still on screen, so its
      // selector hears the arrival first and schedules a spark of its own. By the time these
      // frames run it has been destroyed and has no box to measure, which is what makes it
      // harmless: the arrival is marked as drawn below, where drawing actually happens, so an
      // instance that draws nothing consumes nothing and the one arriving still finds it unspent.
      if (!traced) return;

      lastAccount = this.account;
      this.shimmer = traced;
      this.followBox();
    },

    // Measured off this control's own box and followed for as long as the spark plays: the drawer
    // collapses and the field inside can change what it reports, so the size is not a thing to
    // take once. Re-tracing costs the animation nothing, the path declaring its length as 1, so
    // the dash stays where it was along it whatever the box measures.
    traceBox: function () {
      const box = this.$refs.box;
      const bounds = box && box.getBoundingClientRect();

      if (!bounds || !bounds.width || !bounds.height) return null;

      const width = bounds.width + SPARK_BLEED * 2;
      const height = bounds.height + SPARK_BLEED * 2;
      const radius =
        parseFloat(window.getComputedStyle(box).borderTopLeftRadius) || 0;

      return {
        width,
        height,
        outline: roundedBoxPath(width, height, radius, SPARK_BLEED),
      };
    },

    followBox: function () {
      this.shimmerFrame = requestAnimationFrame(() => {
        if (!this.shimmer) return;

        const traced = this.traceBox();

        if (traced) this.shimmer = traced;

        this.followBox();
      });
    },

    stopFollowingBox: function () {
      if (this.shimmerFrame) cancelAnimationFrame(this.shimmerFrame);

      this.shimmerFrame = null;
    },

    onShimmerEnd: function () {
      this.stopFollowingBox();
      this.shimmer = null;
    },

    // Switching accounts is a navigation: the route is the only thing that moves context.
    //
    // Only when the account actually changes -- emptying the box reports a change too, and is
    // still just someone typing.
    enter(accountUuid) {
      if (!accountUuid || accountUuid === this.value) return;

      if (this.$refs.field) this.$refs.field.blur();

      this.$router.push({ path: `/accounts/${accountUuid}` });
    },
  },
};
</script>

<style lang="scss">
.tier-account-selector {
  padding: 16px 16px 8px;
}

.tier-account-selector--small {
  padding: 8px;
}

// A 40px bordered row holding a 22px avatar and the account's name. The horizontal padding is on
// the field rather than here: the menu anchors to the field, so padding here would narrow it.
.tier-account-selector-box {
  display: flex;
  align-items: center;
  height: 40px;
  padding: 8px 0;
  border: 1px solid $penChecksLightGray;
  border-radius: 8px;
  background-color: $white;
  // What the spark inside is placed against.
  position: relative;
}

// The spark, inset so its canvas reaches past the box on every side and the halo has room. Over
// the box rather than in its flow, so it takes no space and cannot move the name beside it.
.tier-account-selector-shimmer {
  position: absolute;
  top: -7px;
  left: -7px;
  pointer-events: none;
  // The halo's outer falloff reaches further than the canvas bleeds, and a browser clips an svg to
  // its own bounds.
  overflow: visible;

  path {
    fill: none;
    // text-link in the design's palette, as on the avatar above.
    stroke: #3d8a93;
    stroke-width: 2;
    stroke-linecap: round;
    // Two animations rather than one, as the design has it: the trim and the fade carry their own
    // easings, and the last stretch eases them differently.
    //
    // The delay is what puts this after the tab background has finished travelling, stated here
    // rather than sequenced in script because nothing waits for the one before to end. Filled
    // both ways so the first keyframe holds through the delay.
    //
    // These two numbers are summed elsewhere: the toast of an arrival is said last, and the store
    // waits this delay plus this duration out before saying it.
    animation: tier-account-selector-trim 1687ms linear 900ms both,
      tier-account-selector-fade 1687ms linear 900ms both;
  }
}

// One lap of the outline, as the design trims it: a short lit segment travelling the whole way
// round. Every length, time and easing below is the design's own, rebased from the 5.2s loop it
// authors them in onto the window this plays over.
//
// Said as a dash whose gap is one whole path length, which is why the path declares its length as
// 1: with a gap that long only one dash can ever be on the path. The offset is the tail, negative
// because it moves forward along it, and at the last step tail and length sum to 1, so head and
// tail land on the end of the path together.
//
// Hidden until the head has opened away from the tail: a dash of no length still paints, the caps
// being round, and would sit on the corner as a dot for the first frames.
@keyframes tier-account-selector-trim {
  0% {
    stroke-dasharray: 0 1;
    stroke-dashoffset: 0;
    visibility: hidden;
    animation-timing-function: cubic-bezier(0.347, 0, 0.68, 0.349);
  }

  7.2% {
    stroke-dasharray: 0.0124 1;
    stroke-dashoffset: 0;
    visibility: visible;
    animation-timing-function: cubic-bezier(0.428, 0, 0.616, 0.845);
  }

  89.9% {
    stroke-dasharray: 0.0236 1;
    stroke-dashoffset: -0.9764;
    visibility: visible;
    animation-timing-function: cubic-bezier(0.316, 0.645, 0.648, 1);
  }

  100% {
    stroke-dasharray: 0 1;
    stroke-dashoffset: -1;
    visibility: hidden;
  }
}

// The fade, on its own track and its own easing, exactly as the design separates it.
@keyframes tier-account-selector-fade {
  0%,
  89.9% {
    opacity: 1;
    animation-timing-function: ease-out;
  }

  97.2%,
  100% {
    opacity: 0;
  }
}

// The menu renders outside this component, so its option is styled without nesting.
.tier-account-selector-option {
  display: block;
  overflow: hidden;
  font-size: 12px;
  line-height: 16px;
  color: $penChecksDark;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.tier-account-selector-avatar {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 128px;
  // brand/lowest-contrast and text/text-link in the design's palette.
  background-color: #ebf3f5;
  color: #3d8a93;
  font-size: 10px;
  line-height: 14px;
}

.tier-account-selector-field {
  // Vuetify sizes a text field for a full-height form control with a floating label, none of
  // which belongs in a 24px row inside a bordered chip.
  flex: 1 1 auto;
  min-width: 0;
  margin: 0 !important;

  padding: 0 !important;

  // Vuetify aligns .v-input to flex-start, leaving the text riding the top of the row.
  align-items: center;
  height: 24px;

  .v-input__control,
  .v-input__slot {
    min-height: 0 !important;
    align-items: center;
  }

  .v-input__control {
    height: 24px;
  }

  .v-input__slot {
    // The menu anchors to this element and takes its width from it, so it has to span the whole
    // chip; padding keeps its contents in without narrowing it.
    padding: 0 8px !important;
    box-shadow: none !important;
    background: transparent !important;

    // The field's underline, and the thicker one on focus: the chip already has a border.
    &::before,
    &::after {
      display: none !important;
    }
  }

  // The selection area: 24px tall, one line of 12px text, centred.
  .v-input__slot,
  .v-select__slot {
    height: 24px;
    align-items: center;
  }

  .v-select__selections {
    align-items: center;
    flex-wrap: nowrap;
    min-height: 0 !important;
    padding: 0 !important;
    line-height: 16px;
  }

  .v-select__selection--comma {
    margin: 0;
    max-width: 100%;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  // While typing, the name steps aside: Vuetify gives the input a 64px minimum once focused, and
  // the row is barely wider than the name, so leaving both in pushes the typing out of sight.
  &.v-input--is-focused .v-select__selection--comma {
    display: none;
  }

  .v-select__slot > input {
    margin-top: 0 !important;
  }

  input,
  .v-select__selection {
    height: 24px;
    padding: 0;
    font-size: 12px;
    line-height: 16px;
    color: $penChecksDark;
  }

  input::placeholder {
    color: $penChecksDark;
    opacity: 1;
  }

  // The chevron sits inside this field rather than beside it.
  .v-input__append-inner {
    align-self: center;
    margin: 0 0 0 8px;
    padding: 0;
  }

  // Vuetify drops the prepend slot 17px for a full-height enclosed field, which is why every side
  // is stated rather than just the gap.
  .v-input__prepend-inner {
    align-self: center;
    margin: 0 !important;
    margin-right: 6px !important;
    padding: 0 !important;
  }

  // Never the accent Vuetify tints it with on focus: here the chevron is part of the chip, not a
  // state indicator.
  .v-input__append-inner .v-icon {
    font-size: 16px;
    color: $penChecksDarkGray !important;
  }

  .v-text-field__details {
    display: none;
  }
}
</style>
